import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Card from 'reactstrap/lib/Card';

import {
  IncentivesCreativeConfigDefaultProps,
  IncentivesCreativeConfigPropTypes,
} from 'client/data/models/native-ads-creative-config';
import {
  CommonAdDefaultProps,
  CommonAdPropTypes,
  SiteServedAdDefaultProps,
  SiteServedAdPropTypes,
} from 'site-modules/shared/components/ad-unit/ad-unit-prop-types';
import { INCENTIVES_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { DfpTracking } from 'client/site-modules/shared/components/native-ad/utils/dfp-tracking';
import { Disclaimer } from 'client/site-modules/shared/components/native-ad/native-ad-components/disclaimer/disclaimer';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { addZipCode, definePlatform } from 'site-modules/shared/components/native-ad/utils/utils';
import {
  getDisplayHeadline,
  getTrackingValue,
} from 'site-modules/shared/components/native-ad/utils/offer-incentives-ad-utils';
import { siteServedAdTrackingWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-tracking-wrapper/site-served-ad-tracking-wrapper';
import { ConquestRibbon } from 'site-modules/shared/components/native-ad/native-ad-components/conquest-ribbon/conquest-ribbon';
import { CtaButtonText } from 'site-modules/shared/components/native-ad/native-ad-components/cta-button-text/cta-button-text';

import { Styles } from './incentives-site-served-ad-styles';

const DEFAULT_HEADLINE = 'See Offers %%PATTERN:zip%%';

export function IncentivesSiteServedAdAdsFeaturesSpecsUI({
  adRef,
  adClickTrackingListener,
  adLoadTrackingListener,
  adTrackers,
  creativeConfig = IncentivesCreativeConfigDefaultProps,
  isConquest = SiteServedAdDefaultProps.isConquest,
  customTargeting = CommonAdDefaultProps.customTargeting,
  mobile = SiteServedAdDefaultProps.mobile,
  position = SiteServedAdDefaultProps.position,
  cardWrapper = null,
}) {
  const {
    siteServedAdsUpdater,
    zipCode,
    ipZipCode,
    offersNumber,
    location,
    creativeConfigData: {
      logo,
      linkDisclaimer,
      disclaimerCopy,
      headline,
      headline2,
      linkDisplayUrl,
      cta,
      lineItemId,
      siteServedCreativeId,
      target,
    },
    traffickingData: { clickTracker },
  } = creativeConfig;

  const loadTrackingListener = useCallback(
    event => {
      const trackingValue = getTrackingValue(headline, offersNumber, zipCode, ipZipCode, DEFAULT_HEADLINE, 15, 15);
      adLoadTrackingListener(event, trackingValue);
    },
    [adLoadTrackingListener, headline, ipZipCode, offersNumber, zipCode]
  );

  if (!logo || !cta) {
    return null;
  }
  const displayHeadline = getDisplayHeadline(
    headline,
    offersNumber,
    zipCode,
    ipZipCode,
    DEFAULT_HEADLINE,
    15,
    15,
    location
  );

  const ctaURL = addZipCode(clickTracker[definePlatform(mobile)], zipCode);
  const ctaTarget = target || '_blank';
  const nativeStyle = INCENTIVES_AD.NATIVE_STYLE;

  const linkDisplay = (
    <div className="text-blue-40 text-center" style={{ ...Styles.mt_0_25, ...Styles.disclaimer }}>
      {linkDisplayUrl}
    </div>
  );

  const conquestRibbonStyle = { ...Styles.conquestRibbon, marginLeft: mobile ? '-16px' : '-24px' };
  const conquestRibbon = <ConquestRibbon style={conquestRibbonStyle} />;

  const isWiredRetention = !mobile && !isConquest;
  const cardStyle = {
    paddingBottom: isWiredRetention ? '20px' : '12px',
    paddingTop: isWiredRetention ? '20px' : '8px',
    borderRadius: '8px',
    border: 'none',
  };
  const wiredHeadlineStyles = Styles.headlineAds10735;

  return (
    <Card
      className={classnames('incentives-site-served pos-r w-100', cardWrapper, {
        'pe-1_5': isWiredRetention,
        'px-1_5': isConquest && !mobile,
        'px-1': mobile,
      })}
      data-tracking-parent={nativeStyle}
      data-viewability={`${nativeStyle}-${position}`}
      innerRef={adRef}
      style={cardStyle}
    >
      <DfpTracking
        lineItemId={lineItemId}
        siteServedCreativeId={siteServedCreativeId}
        mobile={mobile}
        nativeStyle={nativeStyle}
        position={position}
        customTargeting={customTargeting}
        slotRenderEndListener={loadTrackingListener}
        siteServedAdsUpdater={siteServedAdsUpdater}
      />
      {adTrackers}
      <a
        className="site-served-ad-link no-focus text-decoration-none d-block"
        href={ctaURL}
        target={ctaTarget}
        rel="noopener noreferrer"
        onClick={adClickTrackingListener}
      >
        {!isWiredRetention && (
          <div
            className="w-100 d-flex justify-content-between align-items-center mb-0_5"
            style={
              mobile
                ? {
                    marginTop: '4px',
                    marginBottom: '16px',
                  }
                : Styles.my_0_25
            }
          >
            {isConquest ? conquestRibbon : <img style={Styles.oemLogo} alt="" src={logo} loading="lazy" />}
          </div>
        )}
        <div
          className={classnames({
            'd-flex justify-content-between': !mobile,
          })}
        >
          {isWiredRetention && (
            <div
              className={classnames('d-flex justify-content-between align-items-center mb-0_5', {
                'ms-1 me-1_5 d-flex align-items-center': !isConquest,
              })}
              style={Styles.my_0_25}
            >
              <img style={Styles.oemLogo} alt="" src={logo} loading="lazy" />
            </div>
          )}
          <div
            className={classnames({
              'mt-0_5': isWiredRetention,
            })}
            style={isWiredRetention ? { flex: '1 1 auto' } : {}}
          >
            <div
              className="headline text-cool-gray-10 fw-bold mb-0_25"
              style={{ ...Styles.mb_0_25, ...(mobile ? Styles.headline_mobile : wiredHeadlineStyles) }}
            >
              <ContentFragment>{displayHeadline}</ContentFragment>
            </div>
            <div
              className="small text-cool-gray-30"
              style={{
                fontSize: '1rem',
                lineHeight: '1.375rem',
              }}
            >
              <ContentFragment>{headline2}</ContentFragment>
            </div>
          </div>
          <div
            style={{
              ...Styles.alignSelfStart,
              ...(linkDisplayUrl ? null : Styles.withoutLinkDisplayUrl[mobile]),
            }}
            className={classnames({
              'w-100': mobile,
            })}
          >
            <div
              className={classnames('btn text-nowrap text-transform-none size-16 py-0_75 px-1', {
                'btn-blue-50': !isConquest,
                'btn-outline-blue-50': isConquest,
                'w-100 mt-1_25': mobile,
              })}
              style={{ ...Styles.fontWeight500, ...Styles.py_0_75 }}
            >
              <CtaButtonText text={cta} />
            </div>
            {linkDisplay}
          </div>
        </div>
      </a>
      {disclaimerCopy && <Disclaimer linkDisclaimer={linkDisclaimer} disclaimerText={disclaimerCopy} />}
    </Card>
  );
}

IncentivesSiteServedAdAdsFeaturesSpecsUI.propTypes = {
  ...SiteServedAdPropTypes,
  customTargeting: CommonAdPropTypes.customTargeting,
  creativeConfig: IncentivesCreativeConfigPropTypes,
  cardWrapper: PropTypes.string,
  vehicle: PropTypes.shape({}),
};

export const IncentivesSiteServedAdFeaturesSpecs = siteServedAdTrackingWrapper(
  IncentivesSiteServedAdAdsFeaturesSpecsUI
);
